import React, {
  KeyboardEvent,
  ClipboardEvent,
  useMemo,
  useState,
  useCallback,
  useEffect,
  useContext,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { Editable, Slate, withReact, ReactEditor } from 'slate-react';
import { withHistory } from 'slate-history';
import { createEditor, Descendant, Editor, Range, Transforms } from 'slate';
import styled from 'astroturf/react';
import {
  withCustomElements,
  serializeContent,
  normalizeContent,
  mod,
  interceptPaste,
  checkAutocomplete,
  detectAcademiaPaperLinks,
} from './utils';
import { decorate } from './decorate';
import { AutocompleteType, FollowableUser, Hashtag, Tag } from '../types';
import { TagSelector } from 'js/social/shared/SocialEditor/TagSelector';
import { useTagSearch } from 'js/social/shared/SocialEditor/useTagSearch';
import { TruncateHeight } from 'js/social/shared/SocialEditor/TruncateHeight';
import { FeedContext } from 'js/social/contexts';
import { renderElement, renderLeaf } from './render';
import { HashtagSelector } from 'js/social/shared/SocialEditor/HashtagSelector';
import { useHashtagSearch } from 'js/social/shared/SocialEditor/useHashtagSearch';
import _EditableContainer from "SocialEditor-EditableContainer.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/shared/SocialEditor/SocialEditor.tsx?styleId=EditableContainer";
import _SeeMore from "SocialEditor-SeeMore.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/shared/SocialEditor/SocialEditor.tsx?styleId=SeeMore";


const EditableContainer = /*#__PURE__*/styled(Editable, null, {
  displayName: "EditableContainer",
  styles: _EditableContainer
});

export const SeeMore = /*#__PURE__*/styled("div", null, {
  displayName: "SeeMore",
  styles: _SeeMore
});

export type SocialEditorHandle = {
  focus: () => void;
};

type Props = {
  content: string;
  tags: Tag[];
  allowedHashtags?: string[];
  placeholder?: string;
  disableTags?: boolean;
  disableHashtags?: boolean;
  collapsible?: boolean;
  readOnly?: boolean;
  autoFocus?: boolean;
  lines?: number;
  lineHeight?: number;
  trackImpressions?: boolean;
  impressionExtraData?: object;
  impressionSourceRoot?: string;
  containerClassName?: string;
  onChange?: ({ content, tags }: { content: string; tags: Tag[] }) => void;
  onSubmit?: () => void;
};

export const SocialEditor = forwardRef<SocialEditorHandle, Props>(
  (
    {
      content,
      tags,
      allowedHashtags,
      placeholder,
      disableTags = false,
      disableHashtags = false,
      collapsible = false,
      readOnly = false,
      autoFocus = true,
      lines,
      lineHeight = 20,
      trackImpressions = false,
      impressionExtraData,
      impressionSourceRoot,
      containerClassName = '',
      onChange = () => null,
      onSubmit = () => null,
    },
    ref,
  ) => {
    const [search, setSearch] = useState<string>();
    const [searchType, setSearchType] = useState<AutocompleteType>();
    const [searchRange, setSearchRange] = useState<Range>();
    const [searchIndex, setSearchIndex] = useState(0);
    const { users, isLoading: usersLoading } = useTagSearch(
      searchType === 'tag' ? search : undefined,
      tags,
    );
    const { hashtags, isLoading: hashtagsLoading } = useHashtagSearch(
      searchType === 'hashtag' ? search : undefined,
    );
    const {
      userActions: { setMoreUsers },
    } = useContext(FeedContext);

    const value = useMemo(() => {
      return serializeContent(content, tags);
    }, [content, tags]);

    const editor = useMemo(() => {
      return withCustomElements(withReact(withHistory(createEditor())));
    }, []);

    const updateValue = () => {
      // Notify parent about the change
      onChange(normalizeContent(editor));

      const { type, range, search } = checkAutocomplete(
        editor,
        disableTags,
        disableHashtags,
      );

      setSearchType(type);
      setSearchRange(range);
      setSearch(search);
      setSearchIndex(0);

      const links = detectAcademiaPaperLinks(editor);
      if (links.length >= 1) {
        // This will trigger the updateValue callback which will search for
        // and add subsequent links in their proper place.
        addPaperLink(links[0]);
      }
    };

    const addTag = useCallback(
      (user: FollowableUser) => {
        if (searchType === 'tag' && searchRange) {
          setMoreUsers([user]);
          const newTag: Descendant[] = [
            {
              type: 'tag',
              data: {
                tagId: user.id,
                tagType: 'user',
              },
              children: [{ text: '' }],
            },
            { text: ' ' },
          ];
          Transforms.select(editor, searchRange);
          Transforms.insertNodes(editor, newTag);
          Transforms.move(editor);
          setSearchRange(undefined);
        }
      },
      [editor, searchType, searchRange, setMoreUsers],
    );

    const addHashtag = useCallback(
      (hashtag: Hashtag) => {
        if (searchType === 'hashtag' && searchRange) {
          Transforms.select(editor, searchRange);
          Transforms.insertText(editor, `#${hashtag.hashtag} `);
          Transforms.move(editor);
          setSearchRange(undefined);
        }
      },
      [editor, searchType, searchRange],
    );

    const addPaperLink = useCallback(
      (link: { range: Range }) => {
        const paperLinkNodes: Descendant[] = [
          {
            type: 'paper_link',
            data: link,
            children: [{ text: '' }],
          },
          { text: '' },
        ];

        Transforms.insertNodes(editor, paperLinkNodes, { at: link.range });
        // Move the selection to the new empty node directly after the paper_link node.
        const newPath = [...link.range.anchor.path];
        newPath[newPath.length - 1] = newPath[newPath.length - 1] + 2;
        Transforms.select(editor, {
          anchor: { path: newPath, offset: 0 },
          focus: { path: newPath, offset: 0 },
        });
      },
      [editor],
    );

    const handleKeyDown = useCallback(
      (event: KeyboardEvent) => {
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          // Skip void elements (tags, papers) when using arrow keys
          const { selection } = editor;
          if (!selection || event.metaKey || event.ctrlKey) return;

          const collapsed = Range.isCollapsed(selection);
          if (!collapsed && !event.shiftKey) return;

          const direction =
            event.key === 'ArrowLeft' ? Editor.before : Editor.after;
          const nextFocus = direction(editor, selection.focus);
          if (Editor.void(editor, { at: nextFocus }) && nextFocus) {
            event.preventDefault();
            const newFocus = direction(editor, nextFocus);
            if (newFocus) {
              Transforms.select(editor, {
                anchor: event.shiftKey ? selection.anchor : newFocus,
                focus: newFocus,
              });
            }
          }
        } else if (searchRange) {
          if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            setSearchRange(undefined);
          } else if (event.key === 'ArrowDown') {
            event.preventDefault();
            setSearchIndex(searchIndex + 1);
          } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setSearchIndex(searchIndex - 1);
          } else if (event.key === 'Enter' || event.key === 'Tab') {
            if (searchType === 'tag' && users.length > 0) {
              event.preventDefault();
              const index = mod(searchIndex, users.length);
              addTag(users[index]);
            } else if (searchType === 'hashtag' && hashtags.length > 0) {
              event.preventDefault();
              const index = mod(searchIndex, hashtags.length);
              addHashtag(hashtags[index]);
            }
          }
        } else if (event.metaKey && event.key === 'Enter') {
          onSubmit();
        }
      },
      [
        editor,
        users,
        hashtags,
        searchType,
        searchRange,
        searchIndex,
        addTag,
        addHashtag,
        onSubmit,
      ],
    );

    const handlePaste = useCallback(
      (event: ClipboardEvent) => {
        interceptPaste(editor, event);
      },
      [editor],
    );

    const focus = useCallback(() => {
      if (!readOnly) {
        ReactEditor.focus(editor);
        Transforms.select(editor, Editor.end(editor, []));
      }
    }, [editor, readOnly]);

    // Place cursor at end of content initially (only if autoFocus is enabled)
    useEffect(() => {
      if (autoFocus) {
        focus();
      }
    }, [autoFocus, focus]);

    useImperativeHandle(ref, () => ({ focus }));

    const popoverRect = useMemo(() => {
      if (!searchRange) {
        return null;
      }

      const domRange = ReactEditor.toDOMRange(editor, {
        anchor: searchRange.anchor,
        focus: searchRange.anchor,
      });
      return domRange.getBoundingClientRect();
    }, [editor, searchRange]);

    return (
      <Slate editor={editor} value={value} onChange={updateValue}>
        {readOnly && lines !== undefined ? (
          <TruncateHeight
            height={lines !== undefined ? lines * lineHeight : undefined}
            collapsible={collapsible}
            styledMoreElement={<SeeMore />}
            readMoreImpressionProps={{
              active: trackImpressions,
              source: impressionSourceRoot + '_seemore',
              extra: impressionExtraData,
            }}
          >
            <EditableContainer
              readOnly={readOnly}
              placeholder={placeholder}
              className={containerClassName}
              decorate={decorate(
                editor,
                disableHashtags ? [] : allowedHashtags,
                readOnly,
              )}
              autoFocus={autoFocus}
              renderElement={renderElement}
              renderLeaf={renderLeaf}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
            />
          </TruncateHeight>
        ) : (
          <EditableContainer
            readOnly={readOnly}
            placeholder={placeholder}
            className={containerClassName}
            decorate={decorate(
              editor,
              disableHashtags ? [] : allowedHashtags,
              readOnly,
            )}
            autoFocus={autoFocus}
            renderElement={renderElement}
            renderLeaf={renderLeaf}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
          />
        )}

        {!readOnly && searchType === 'tag' && popoverRect && (
          <TagSelector
            anchorTop={popoverRect.top}
            anchorLeft={popoverRect.left}
            users={users}
            tags={tags}
            loading={usersLoading}
            activeIndex={searchIndex}
            onActiveIndexChange={setSearchIndex}
            onSelect={addTag}
          />
        )}

        {!readOnly && searchType === 'hashtag' && popoverRect && (
          <HashtagSelector
            anchorTop={popoverRect.top}
            anchorLeft={popoverRect.left}
            hashtags={hashtags}
            loading={hashtagsLoading}
            activeIndex={searchIndex}
            onActiveIndexChange={setSearchIndex}
            onSelect={addHashtag}
          />
        )}
      </Slate>
    );
  },
);
